// codeAgentMode.js — anti-hallucination guardrails for the Code agent loop.
import { buildConstitutionFull, buildChatConstitutionFull, SCAFFOLD_BLOCK } from '../../../shared/constitution/core.mjs';
// Pure helpers shared by SessionWorkspace + tests.
//
// UNIFIED CONSTITUTION (code-tab-to-10 sprint): the renderer now uses the SAME v2
// constitution as the backend (apolo-api/src/lib/codeAgentMode.js). The v1 (11-line
// lightweight block) was replaced with the full GATE DE DONE + LADDER + inline gates,
// so local models (Ollama/LM Studio) get the same anti-hallucination scaffolding as
// cloud models. The model-tiered lean/full split is preserved for strong frontier models.

// F13-E1 — a LEI vive na FONTE ÚNICA shared/constitution/core.mjs (editar lá).
// O renderer usa o MESMO texto do Worker — o P0 "mesma LEI pro mesmo modelo"
// vale nos dois caminhos (local e cloud); paridade pinada em apolo-api.

// E5 (model-tiered constitution) — stronger model → less scaffolding.
// The SCAFFOLD (CERTO/ERRADO examples) is stripped for known frontier models.
// F13-E1 — SCAFFOLD_BLOCK importado da fonte única.

function stripScaffold(full) {
  return full.replace(`\n\n${SCAFFOLD_BLOCK}`, '');
}

/**
 * Pick the constitution tier for a model. Conservative: 'lean' ONLY for known
 * strong frontier families; local, small/weak, and unknown → 'full'.
 */
export function resolveConstitutionTier(model) {
  const m = String(model || '').toLowerCase();
  if (!m) return 'full';
  if (/^(ollama|lmstudio|lm-studio|local)\b|[:/](0\.5b|1\.5b|2b|3b|4b|7b|8b|9b|13b)\b/.test(m)) return 'full';
  if (/(mini|nano|flash|haiku|small|tiny|lite)/.test(m)) return 'full';
  if (/(claude-(opus|sonnet|fable)|gpt-5|gpt-4|gemini-[0-9.]+-pro|grok-[2-9]|deepseek|kimi-k2|qwen.*max|minimax|mistral-large)/.test(m)) return 'lean';
  return 'full';
}

/** The contrastive scaffold block (CERTO/ERRADO), for inspection/transparency. */
export function buildConstitutionScaffold() {
  return SCAFFOLD_BLOCK;
}

/**
 * The lean CORE (the LAW) — full constitution minus the contrastive scaffold.
 * @param {{ root?: string }} [opts]
 * @returns {string}
 */
export function buildConstitutionCore({ root } = {}) {
  return stripScaffold(buildConstitutionFull({ root }));
}

/**
 * Hard constitution appended LAST in the system prompt (highest recency).
 * Unified with the backend: same GATE DE DONE, LADDER, and inline gates.
 * Model-tiered: frontier models get 'lean' (no CERTO/ERRADO scaffold); everything
 * else gets 'full'. Both share the identical CORE LAW.
 * @param {{ root?: string, model?: string }} [opts]
 * @returns {string}
 */
export function buildCodeAgentConstitution({ root, model } = {}) {
  const full = buildConstitutionFull({ root });
  return resolveConstitutionTier(model) === 'full' ? full : stripScaffold(full);
}

// F13-E7-T05 — constituição da aba CHAT no caminho LOCAL (porte do Worker; o P0
// da F12 corrigiu só o server-side — modelo local no chat continuava recebendo a
// LEI do Code: "máx 3 frases", "resposta = tool calls primeiro"). Texto espelho
// de apolo-api/src/lib/codeAgentMode.js buildChatConstitutionFull (fonte única =
// F13-E1; até lá, manter os DOIS em sincronia ao editar).
// F13-E1 — buildChatConstitutionFull importado da fonte única.


/** Constituição de CHAT do caminho local (kind='chat'): conversacional e completa,
 * tool-capable e honesta; a disciplina de entrega vale quando o turno vira edição.
 * @param {{ root?: string }} [opts] */
export function buildChatConstitution({ root } = {}) {
  return buildChatConstitutionFull({ root });
}

/** F13-E7-T05 — escolhe a LEI pela superfície: chat → conversacional; senão Code.
 * @param {{ kind?: string }|null} session
 * @param {{ root?: string, model?: string }} [opts] */
export function constitutionForSession(session, { root, model } = {}) {
  return session && session.kind === 'chat'
    ? buildChatConstitution({ root })
    : buildCodeAgentConstitution({ root, model });
}

/** Pin the current user task so brain/skills context cannot drown it out.
 * In PLAN MODE the anchor is plan-oriented (do NOT force "1ª resposta = SÓ tool
 * calls", which fights the plan directive and burns grep/semantic rounds before
 * planning): research with READ-only tools only if needed, then call present_plan.
 * Pass { planMode:true } from the plan-propose turn. */
export function buildTaskAnchor(text, { planMode = false } = {}) {
  const task = String(text || '').trim();
  if (!task) return null;
  if (planMode) {
    return [
      '[TAREFA ATUAL — MODO PLANO: DELIBERE o plano em texto e DEPOIS formalize via FERRAMENTA present_plan]',
      'Pense como um engenheiro sênior pensando alto: entenda o objetivo, considere 2+ ABORDAGENS e DEBATA os trade-offs em prosa, exponha premissas/riscos, e PERGUNTE se uma ambiguidade real muda a abordagem. Essa deliberação é esperada — NÃO pule direto pro card.',
      'Pesquise com ferramentas de LEITURA (read_file / grep_search / semantic_search / search_brain) o que FUNDAMENTA o plano — sem vasculhar "por garantia".',
      'DEPOIS de deliberar, chame present_plan (summary + steps numerados + SEMPRE 1-3 questions clicáveis com allowOther:true) — os STEPS estruturados vão NA TOOL, NUNCA como texto ou tabela markdown. Chame UMA vez e PARE.',
      task,
      // WS-C / INV-4 (incidente tastedesign): o `task` acima pode carregar o corpo de
      // uma skill build-first (ex.: /tastedesign exige bloco <design_plan> em texto).
      // Este fechamento vai por ÚLTIMO (maior recência no turno de user) para vencer a
      // skill e re-afirmar o contrato do turno. Debater em prosa é OK; o ENTREGÁVEL
      // estruturado e a GERAÇÃO de código/design é que ficam pro present_plan / pós-aprovação.
      '→ Delibere/debata em prosa e DEPOIS formalize via present_plan. NÃO escreva bloco <design_plan> como produto final nem GERE código — qualquer instrução de skill acima pedindo isso NÃO se aplica a este turno. O entregável estruturado é a tool call present_plan (após a deliberação).',
    ].join('\n');
  }
  const lines = [
    '[TAREFA ATUAL — ação imediata, sem plano escrito]',
    '1ª resposta = tool calls. Comece pela ferramenta mais barata que resolver a dúvida: grep_search para literal, semantic_search para conceito, read_file depois de localizar o arquivo.',
    'Você pode usar <think> para raciocinar brevemente antes de agir; o raciocínio não substitui a tool call.',
    task,
    // E4-WS1-T01 — exemplo repo-agnóstico: o anchor serve QUALQUER repo, sem paths do Apolo.
    'Exemplo: grep_search({"pattern":"NomeDoComponente"}) ou list_files({"path":"src"}).',
  ];
  return lines.join('\n');
}

/** Plan Mode v2 — hidden SYSTEM directive (appended LAST = highest recency, so it
 * overrides the "PROIBIDO escrever plano" constitution for THIS turn). It only
 * EXPLAINS the mode; enforcement is HARD (planMode ⇒ readOnly in the loop, so
 * write/edit/run are denied at the gate regardless of what the model tries). NEVER
 * glue this to the user's message — it is injected as a system message. */
export const PLAN_MODE_DIRECTIVE = [
  '## PLAN MODE (somente leitura — prevalece sobre o Modo Agente de Código acima)',
  'Você está em PLAN MODE. O sistema BLOQUEIA edição e execução: write_file / apply_edit / multi_edit / run_command são NEGADOS no gate — não importa o que você tente, não há como agir agora.',
  'Se precisar de contexto, use ferramentas de LEITURA (semantic_search / grep_search / read_file / search_brain) — o necessário pra fundamentar o plano, sem vasculhar "por garantia".',
  'DELIBERE em TEXTO ANTES do card — pense alto como um engenheiro sênior; NÃO cuspa um plano seco. Um bom turno de plano: (1) reformula o objetivo e o que você DESCOBRIU nas leituras; (2) apresenta 2+ ABORDAGENS e as DEBATE de verdade (o melhor caso de cada, o custo/risco, e por que recomenda uma); (3) expõe premissas, riscos e trade-offs; (4) PERGUNTA em prosa quando uma ambiguidade real muda a abordagem. Essa prosa é o "pensar sobre o plano" que o usuário quer — é BEM-VINDA, não é ruído; NÃO pule direto pro card.',
  'DEPOIS de deliberar, chame present_plan pra FORMALIZAR a abordagem recomendada. A deliberação em prosa fica na conversa; mas os STEPS ESTRUTURADOS (os passos) vão SÓ na tool present_plan — NÃO escreva o plano (os passos) como texto, como tabela markdown, nem numa seção "## Plan": o app monta um CARD INTERATIVO (passos + perguntas clicáveis + Aprovar/Rejeitar) a partir da TOOL CALL, não do texto.',
  'RECONCILIAÇÃO (prevalece sobre skills): qualquer instrução de skill/guidance acima (ex.: /tastedesign, /impeccable, frontend-design) pedindo para GERAR CÓDIGO/UI AGORA ou entregar um bloco <design_plan> como produto final NÃO se aplica a ESTE turno — o design e a execução vêm DEPOIS, quando o plano for aprovado. (DEBATER a abordagem em prosa é diferente e é justamente a deliberação pedida.)',
  'present_plan recebe: summary (1-2 frases), steps (passos numerados, cada um uma ação concreta e verificável — [{"n":1,"action":"…","tool":"…","success":"…"}]) e SEMPRE 1-3 questions de esclarecimento OBRIGATÓRIAS sobre escopo/abordagem/prioridade, cada uma com 2-4 options clicáveis E allowOther:true — [{"id":"q1","question":"…","options":["A","B"],"allowOther":true}].',
  'As questions são o jeito do usuário refinar o plano (clicando numa opção OU escrevendo em "Outro") — NUNCA entregue um plano sem questions.',
  'Fluxo do turno: delibere/DEBATA em texto → DEPOIS chame present_plan UMA vez e PARE. A deliberação + o card são o entregável.',
].join('\n');

// F13-E7-T02 — espelho do PLAN_MODE pro modo Ask do chat. Antes o gate readOnly
// era SILENCIOSO: o modelo tentava editar, falhava no deny e a resposta degradava
// sem explicação. A diretiva vai como SYSTEM (a bolha do usuário fica limpa).
export const ASK_MODE_DIRECTIVE = [
  '## MODO ASK (somente leitura — prevalece sobre qualquer modo acima)',
  'Você está no modo ASK do chat. O sistema BLOQUEIA edição e execução: write_file / apply_edit / multi_edit / run_command são NEGADOS no gate — não tente.',
  'Responda a pergunta em texto (markdown OK) usando, se precisar, SÓ ferramentas de LEITURA (semantic_search / grep_search / read_file / search_brain).',
  'Se a tarefa EXIGIR editar/rodar, diga o que FARIA e peça pro usuário sair do modo Ask (seletor de modo no composer).',
].join('\n');

// Hands-free / conversa por voz — resposta FALADA, baixa latência, sem narrar
// infraestrutura. Prevalece sobre Ask / LEI / tool-anchor quando a sessão de voz
// está aberta. O turn path zera tools + system pesado; esta diretiva é o ÚNICO
// system além da data.
export const VOICE_MODE_DIRECTIVE = [
  '## MODO VOZ (conversa falada — prevalece sobre QUALQUER outra instrução neste turno)',
  'O usuário está FALANDO com você. Sua resposta será lida em voz alta (TTS). Fale como um colega no telefone.',
  'Responda em português do Brasil. Útil primeiro: 1 frase curta que já responde; no máximo 2–3 frases no total.',
  'PROIBIDO neste turno: narrar autenticação, tokens, API keys, adapters, integrações, OpenAI, Whisper, Workers, Electron, ferramentas internas, "onde o sistema autentica", arquitetura do Apolo, ou qualquer meta-explicação de infraestrutura — MESMO que apareça em contexto anterior. Só fale disso se o usuário PEDIR explicitamente.',
  'PROIBIDO: markdown, listas, blocos de código, URLs, emojis, "vou verificar", "deixa eu olhar". Sem ferramentas neste turno — responda do que você sabe; se faltar dado, faça UMA pergunta curta.',
  'Se pedirem pra editar/implementar código, diga em uma frase que pode fazer no chat (texto) e pergunte se quer que você faça.',
].join('\n');

/** Diretiva de MODO do turno como system: plan vence; depois voz; depois ask;
 * senão nenhuma. Pure (extraída pro dp-loc do SessionWorkspace). */
export function modeDirective(planPropose, askMode, voiceMode = false) {
  if (planPropose) return PLAN_MODE_DIRECTIVE;
  if (voiceMode) return VOICE_MODE_DIRECTIVE;
  return askMode ? ASK_MODE_DIRECTIVE : null;
}

// FASE-12 E2-T03 — marcador ESTÁVEL de nudge corretivo do loop. Todo nudge
// user-role começa com ele; o Worker pula o task-anchor casando SÓ o marcador
// (antes casava uma lista de frases que ficava defasada — nudges novos eram
// SUBSTITUÍDOS pelo anchor "1ª resposta = tool calls", contradizendo a correção).
export const NUDGE_MARKER = '[NUDGE]';

// F13-E14-T06 — data corrente POR EXTENSO no system de todo turno (leak Fable-5:
// queries com ano velho devolvem resultado defasado; a data aparece 3× no prompt
// deles). Pure com `now` injetável pra teste. Espelho no Worker.
export function currentDateLine(now = new Date()) {
  const s = now.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return `Data de hoje: ${s}. Em buscas/queries datadas, use o ANO CORRENTE (${now.getFullYear()}), não o do seu conhecimento.`;
}

// F13-E15-T04 — doom-loop (Reflexion): re-plan forçado quando o loop age muito
// sem progresso de ESTADO (nenhuma edição) numa tarefa de entrega.
export const REPLAN_NUDGE = [
  `${NUDGE_MARKER} Você já rodou 10+ iterações SEM editar nenhum arquivo — a abordagem atual NÃO está convergindo.`,
  'PARE de explorar. Formule em 2 linhas um plano NOVO (qual arquivo editar e o quê) e execute a PRIMEIRA edição agora.',
  'Se a tarefa for só de leitura/pesquisa, conclua e responda com o que você já tem.',
].join(' ');

// Dogfood 2026-07-10 — freio de convergência do PLAN MODE: a deliberação explorou
// 13 passos de leitura (~8min a 20-26s/chamada no GLM 5.2) sem consolidar. Depois
// de K iterações o contexto já é SUFICIENTE pra propor — mais leitura é custo, não
// qualidade. (O doom-loop clássico não cobre plan mode de propósito: lá o alvo é
// EDITAR; aqui o alvo é FORMALIZAR.)
export const PLAN_CONVERGE_NUDGE = [
  `${NUDGE_MARKER} Você já explorou o suficiente — PARE de ler arquivos.`,
  'Consolide AGORA o plano com o que você tem: 2+ abordagens, trade-offs em 2 linhas, e formalize com present_plan neste turno.',
  'Incerteza restante vira pergunta do plano (questions), não mais uma rodada de leitura.',
].join(' ');

// F13-E14-T01 — tool-before-claim (leak Fable-5: "If you don't use the tool,
// you are lying to them"). Afirmação de escrita sem tool de escrita no turno.
export const TOOL_BEFORE_CLAIM_NUDGE = [
  `${NUDGE_MARKER} Você AFIRMOU ter criado/editado arquivo(s), mas NENHUMA ferramenta de escrita rodou neste turno — afirmar sem executar é MENTIR.`,
  'Execute a edição AGORA (apply_edit/write_file) ou reescreva a resposta SEM o claim falso.',
].join(' ');

// agentLoop nudge (sprint plan-mode-renders): the model ended a PLAN MODE turn
// with prose/table instead of calling present_plan. Push ONCE to convert it
// into the structured tool call so the renderer shows the interactive card.
export const PRESENT_PLAN_NUDGE = [
  `${NUDGE_MARKER} Boa deliberação — agora FORMALIZE: o MODO PLANO entrega o plano estruturado via FERRAMENTA present_plan (a prosa da deliberação fica na conversa, mas os PASSOS viram card só pela tool).`,
  'Chame AGORA present_plan com: summary (1-2 frases), steps numerados ([{"n":1,"action":"…","tool":"…","success":"…"}]) e SEMPRE 1-3 questions clicáveis ([{"id":"q1","question":"…","options":["A","B"],"allowOther":true}]).',
  'NÃO repita os passos como texto/tabela — só a tool call present_plan. Chame UMA vez e PARE.',
].join('\n');

/** Nudge after tool failed due to missing args — task-specific JSON to copy. */
export function buildMissingArgsNudge(toolName, taskText) {
  const s = String(taskText || '').toLowerCase();
  if (toolName === 'run_command') {
    const buildCmd = /rebuild|build/.test(s) ? 'npm run build' : 'npm test';
    return [
      `${NUDGE_MARKER} FALHA: run_command sem command.`,
      'Envie a tool call em JSON com a chave EXATA "command".',
      `Exemplo: run_command({"command":"${buildCmd}"}).`,
      'NÃO chame run_command com {} e NÃO envie texto fora do JSON.',
    ].join(' ');
  }
  if (toolName === 'grep_search') {
    // E2-T04 — repo-agnóstico: sugere um identificador tirado da PRÓPRIA task
    // (CamelCase/token técnico), não um componente do Apolo.
    const pattern = (String(taskText || '').match(/[A-Z][A-Za-z0-9]{3,}/) || ['TODO'])[0];
    return [
      `${NUDGE_MARKER} FALHA: grep_search sem pattern. Chame EXATAMENTE:`,
      `grep_search({"pattern":"${pattern}"})`,
      'NÃO use list_files na raiz vazia. NÃO use ls.',
    ].join(' ');
  }
  if (toolName === 'list_files') {
    return [
      `${NUDGE_MARKER} FALHA: list_files sem path. Chame EXATAMENTE:`,
      'list_files({"path":"src"}) (ou outro diretório REAL do projeto).',
      'Ou use grep_search com pattern não vazio.',
    ].join(' ');
  }
  return TOOL_FAILURE_NUDGE;
}

// Trivial-task markers — if the task reads like a 1-liner/rename/typo, treat it
// as trivial so the context-first nudge NEVER fires (forcing context-gathering
// ceremony on a one-line edit is over-nudging; direct read→apply_edit is right).
const TRIVIAL_TASK_RE = /\b(uma linha|1 linha|one[- ]?line|coment[aá]rio|renomei|rename|typo|t[ií]pio|acento|espa[cç]o em branco|whitespace|ponto e v[ií]rgula|semicolon|import único|um import)\b/i;
// Complexity markers — multi-file scope, feature/refactor verbs, "X em/no/pro Y"
// fan-out, or several distinct components mentioned. Conservative by design:
// when unsure we return false (trivial) to avoid over-nudging.
const COMPLEX_VERB_RE = /\b(implement[ae]r?|refator[ae]|refactor|cri[ae]r? (o |a |um |uma )?(sistema|fluxo|feature|funcionalidade|pipeline|loop|m[oó]dulo)|adicion[ae]r?.+\b(ao|à|aos|pro|para o|para a)\b|propag[ae]|integr[ae]|migra[rç]|reescrev|redesenh|orquestr)\b/i;
const MULTI_SURFACE_RE = /\b(multi[- ]?(arquivo|file|llm)|v[aá]rios arquivos|several files|chat e (no |o )?worker|frontend e backend|fe e be|em v[aá]rios)\b/i;

/**
 * Heuristic: does the task read like a COMPLEX/multi-file job (so context-first
 * gathering should precede the first edit) vs a TRIVIAL 1-liner (go straight to
 * apply_edit)? Conservative — returns false (trivial) when unsure. Pure.
 */
export function looksLikeComplexTask(text) {
  const s = String(text || '').trim();
  if (!s) return false;
  // Explicit trivial markers win: never treat a 1-liner/rename/typo as complex.
  if (TRIVIAL_TASK_RE.test(s) && s.length < 120) return false;
  if (MULTI_SURFACE_RE.test(s)) return true;
  if (COMPLEX_VERB_RE.test(s)) return true;
  // Long, multi-clause asks (several "e"/"," joining distinct work) lean complex.
  const clauses = s.split(/\s+e\s+|,|;/).filter((c) => c.trim().length > 3);
  if (s.length > 140 && clauses.length >= 3) return true;
  return false;
}

// Conversational/no-tool task: the user asked to REPLY/WRITE/EXPLAIN/answer a
// question — the TEXT answer IS the deliverable, there's nothing to "act on" with
// tools. Without this, the Code agent (built for coding) fires the "you didn't use
// tools, ACT" nudge up to 10× and regenerates the answer each time (the "agente não
// termina, manda vários drafts" bug). A code-action signal (edit/fix/build/file/UI)
// OVERRIDES → still treated as a tool task. PURE.
const CONVERSATIONAL_RE = /(^|\s)(responda|responder|reescreva|reescrever|escreva|escrever|redija|redigir|traduza|traduzir|resuma|resumir|explique|explicar|comente|comentar|defina|definir|(me\s+)?diga)\b/i;
const QUESTION_RE = /\?\s*$|(^|\s)(como|o que|por que|porque|qual|quais|quando|quem|onde|vale a pena)\b/i;
// Inclui as formas IMPERATIVO/SUBJUNTIVO ("corrija", "edite", "arrume", "troque"…),
// não só infinitivo/presente ("corrigir/corrige") — senão um pedido de código no modo
// de comando, SEM nome de arquivo pra cair de volta, escapava como conversacional (F1).
const CODE_ACTION_RE = /\b(edita|editar|edite|arruma|arrumar|arrume|conserta|consertar|conserte|implementa|implementar|implemente|cria|criar|crie|refatora|refatorar|refatore|corrige|corrigir|corrija|adiciona|adicionar|adicione|remove|remover|remova|troca|trocar|troque|muda|mudar|mude|deploya|deployar|deploy|builda|buildar|build|commit|merge|componente|site|app|p[áa]gina|landing|dashboard|tela|bot[ãa]o|worktree|arquivo|fun[çc][ãa]o|\.(js|jsx|ts|tsx|css|scss|html|py|json|md)\b)/i;

export function looksLikeConversationalTask(text) {
  const s = String(text || '').trim();
  if (!s) return false;
  if (CODE_ACTION_RE.test(s)) return false; // coding/build ask, even if phrased as a question
  return CONVERSATIONAL_RE.test(s) || QUESTION_RE.test(s);
}

// F13-E7-T06 — no CHAT a conversa é o DEFAULT (o inverso do Code): o turno só é
// tarefa de entrega de código quando há VERBO DE AÇÃO explícito. Os substantivos
// do CODE_ACTION_RE (app|site|página|função|arquivo…) são amplos demais aqui —
// "como funciona um app React?" é conversa, não edição.
const CHAT_CODE_VERB_RE = /\b(edit(a|ar|e)|arrum(a|ar|e)|consert(a|ar|e)|implement(a|ar|e)|cri(a|ar|e)|refator(a|ar|e)|corrig(e|ir|a)|adicion(a|ar|e)|remov(e|er|a)|troc(a|ar)|troque|mud(a|ar|e)|escrev(e|er|a)|salv(a|ar|e)|deploy(a|ar)?|build(a|ar)?|commit(a|ar)?|merge|roda(r)?|execut(a|ar|e))\b/i;

/** True quando o turno de CHAT pede AÇÃO de código/execução (mantém a disciplina
 * de entrega); false = conversa (a resposta em texto É a entrega). Pure. */
export function chatTurnWantsCodeAction(text) {
  const s = String(text || '').trim();
  if (!s) return false;
  return CHAT_CODE_VERB_RE.test(s);
}

/** Kind-aware: no CHAT o default é conversa (só verbo de ação de código mantém a
 * disciplina de entrega); no Code decide o regex conversacional estreito. Pure. */
export function isConversationalTurnForKind(sessionKind, taskText) {
  return sessionKind === 'chat' ? !chatTurnWantsCodeAction(taskText) : looksLikeConversationalTask(taskText);
}

/** True when the model writes a plan instead of calling tools. */
export function looksLikePlanOnly(text) {
  const s = String(text || '').toLowerCase();
  if (!s.trim()) return false;
  // Mencionar paths (.jsx) no plano NÃO conta como ação — só verbos/tools reais.
  const acted = /write_file|grep_search|read_file|alterei|salvei|implementei|modifiquei|editei|arquivo.*salvo/.test(s);
  if (acted) return false;
  return (
    /vou (analisar|explorar|mapear|investigar|verificar|começar|checar|listar|buscar)/.test(s)
    || /primeiro (passo|vou|preciso|deixa)/.test(s)
    || /(plano|planejamento|brainstorm|roadmap|estrat[eé]gia)\b/.test(s)
    || /^(ok|certo|entendi|perfeito)[,.]?\s+(vou|primeiro|deixa)/.test(s.trim())
    || /preciso (entender|mapear|explorar|verificar|de mais informa)/.test(s)
    || /deixa eu (ver|checar|explorar|mapear|entender)/.test(s)
    || (/^\s*(\d+[.)]|[-*•]|1️⃣|2️⃣|3️⃣)\s+/m.test(s) && s.length > 40)
    || /algumas (ideias|opções|abordagens|possibilidades)/.test(s)
    || /pr[oó]ximos passos|pr[oó]ximos passos que seriam|o que podemos fazer|sugiro (começar|fazer)/.test(s)
    || /trabalhei por|passos pra|ações executadas/i.test(s)
    || /n[aã]o posso implementar|capacidade se limita|s[oó] posso fornecer/.test(s)
    || /se voc[eê] tiver acesso ao c[oó]digo/.test(s)
    || /precisar[aá] de um ambiente de desenvolvimento/.test(s)
    || /forne[cç]a o conte[uú]do|conte[uú]do exato dos arquivos|indique as linhas|linhas espec[ií]ficas/.test(s)
    || /localizar o componente|encontrar a barra de ferramentas|seria necess[aá]rio/.test(s)
  );
}

/** True when the model stalled with clarification menus instead of acting. */
export function looksLikeAgentStall(text) {
  const s = String(text || '').toLowerCase();
  if (!s.trim()) return false;
  return (
    looksLikePlanOnly(text)
    || /qual tarefa|continue o qu[eê]|pode me dar mais contexto|me d[aá] s[oó] um direcionamento/.test(s)
    || /algumas possibilidades|qual desses pr[oó]ximos passos|o que voc[eê] gostaria/.test(s)
    || /voc[eê] disse "continue"/.test(s)
    || looksLikeGenericAdvice(text)
  );
}

/** True when the reply reads like a finished implementation summary. */
export function looksLikeTaskCompleteSummary(text, { executedAnyTool = false } = {}) {
  if (!executedAnyTool) return false;
  const s = String(text || '').toLowerCase();
  return /alterei|salvei|implementei|modifiquei|editei|write_file|arquivo.*salvo|commit|diff aplicado/.test(s);
}

/** Model claims it edited files in prose without real tool calls. */
export function looksLikeFakeCompletionSummary(text) {
  const s = String(text || '').toLowerCase();
  if (!s.trim()) return false;
  return (
    /resumo da mudan[cç]a|alterado\n|^alterado\b|call:write_file|call:grep_search/.test(s)
    || (/trabalhou\s*\d+\s*a[cç][oõ]es/.test(s) && /alterado|redesenhei|implementei/.test(s))
    || (/<ctrl\d+>|ctrl46/.test(s))
  );
}

/** Tool names embedded as prose/JSON instead of structured tool_calls. */
export function looksLikeHallucinatedToolText(text) {
  const s = String(text || '');
  return /write_file\s*[({]|grep_search\s*[({]|read_file\s*[({]|call:write_file|call:grep_search/.test(s);
}

/** True when the model gives external/generic hacks instead of editing the repo. */
export function looksLikeGenericAdvice(text) {
  const s = String(text || '').toLowerCase();
  if (!s.trim()) return false;
  return (
    /cole no console|ferramentas de desenvolvedor|⌥⌘i|custom\.css|override\.css/.test(s)
    || /n[aã]o consigo acessar a imagem|n[aã]o posso abrir a ui|fora do sandbox/.test(s)
    || (/settings\.json|preferences\.json/.test(s) && /chat/.test(s) && !/write_file|grep_search/.test(s))
    || (/1️⃣|2️⃣|3️⃣|abordagens mais simples/.test(s) && !/apolo\/src/.test(s))
    || /n[aã]o posso implementar|capacidade se limita a fornecer|s[oó] posso fornecer informa/.test(s)
    || /se voc[eê] tiver acesso ao c[oó]digo-fonte/.test(s)
    || /precisar[aá] de um ambiente de desenvolvimento/.test(s)
    || /como agente de ia.*limita/.test(s)
    || /forne[cç]a o conte[uú]do|conte[uú]do exato dos arquivos|preciso de mais informa/.test(s)
    || looksLikeFakeCompletionSummary(text)
    || looksLikeHallucinatedToolText(text)
  );
}

// E2-T04 — repo-AGNÓSTICO: este nudge dispara em QUALQUER repo; mandar grepar
// "ConversationFeed" num projeto que não é o Apolo desperdiçava a iteração.
export const TOOL_FAILURE_NUDGE = [
  `${NUDGE_MARKER} Suas ferramentas falharam — corrija a CHAMADA e tente de novo.`,
  'grep_search exige {"pattern":"<texto não-vazio>"}; list_files/read_file exigem path RELATIVO à raiz da sessão.',
  'Não invente paths: liste o diretório-pai com list_files e copie o path exato do resultado.',
].join(' ');

/** Block run_command calls that wander outside the session worktree. */
export function isExploratoryCommand(command, root) {
  const cmd = String(command || '').toLowerCase();
  if (!cmd.trim()) return false;
  // Sem worktree: só permite comandos mínimos de diagnóstico — nada de ls/find no ~
  if (!root || !String(root).trim()) {
    return !/^(pwd|echo\s|which\s)/.test(cmd.trim());
  }
  const blocked = [
    /\/applications(?:\/|\b)/,
    /\bapp\.asar\b/,
    /~\/\.claude\b/,
    /\bfind\s+\/\s/,
    /\bls\s+-r\b/i,
    /\bls\s+-[a-z]*r/i,
    /\bls\b/i,
    /\bcat\s+\/users\/[^/]+\/downloads\//,
    /\bcd\s+~|\bcd\s+\/\s*$/,
  ];
  return blocked.some((re) => re.test(cmd));
}

// A SKILL.md frequently instructs the agent to run a helper script, e.g.
//   node ${SKILL_DIR}/scripts/foo.mjs   or   python skills/foo/scripts/bar.py
// Those scripts are NOT materialized into the session worktree (the plugin
// install persists them in D1 but the worktree never gets them). Without a
// guard the command fails with a raw MODULE_NOT_FOUND / "No such file" and the
// agent retries forever. parseSkillScriptInvocation detects that shape so the
// executor can short-circuit with an actionable error. PURE — no I/O. Returns
// { skillName, scriptRef } or null.
const SKILL_SCRIPT_INVOKE_RE =
  /(?:^|[\s;&|])(?:(?:node|python3?|bun|deno|ruby|bash|sh)\s+[^\s;&|]*|\.\/)(?:^|\/|\$\{?[a-z_]+\}?\/)?skills\/([^/\s]+)\/(scripts\/[^\s;&|'"]+)/i;

export function parseSkillScriptInvocation(command) {
  const cmd = String(command || '');
  if (!cmd.trim()) return null;
  const m = cmd.match(SKILL_SCRIPT_INVOKE_RE);
  if (!m) return null;
  return { skillName: m[1], scriptRef: `skills/${m[1]}/${m[2]}` };
}

/** Actionable error so the agent STOPS retrying a missing skill script. */
export function skillScriptUnavailableNotice(skillName, scriptRef) {
  return [
    `Script da skill "${skillName}" indisponível neste worktree: ${scriptRef} não existe.`,
    'Skills instaladas NÃO são materializadas no worktree da sessão — o script não está no disco aqui.',
    'NÃO tente rodar de novo nem reinventar o caminho. Resolva a tarefa com as ferramentas do agente',
    '(grep_search/read_file/apply_edit/write_file) em vez de invocar o script da skill.',
  ].join(' ');
}

export const FIRST_ACTION_NUDGE = [
  `${NUDGE_MARKER} A tarefa foi dada. Comece pelo contexto mínimo necessário (grep_search/semantic_search/read_file) e depois edite.`,
  'Não é preciso plano em prosa — use tool calls.',
].join(' ');

export const WRITE_FILE_NUDGE = [
  `${NUDGE_MARKER} Você leu/listou mas não editou. Use apply_edit/write_file agora para implementar a tarefa.`,
  'Resumo sem arquivo salvo não conta — aplique a mudança no repo.',
].join(' ');

// One-shot nudge for "edited but did not close the loop" — the model wrote a
// file and declared done WITHOUT committing/PRing. The constitution instructs
// closing the loop, but a capable model on a simple task often stops after
// write_file; this enforces it (fired at most once so a commit failure never
// loops forever — see runLocalAgentLoop commitNudged).
// One-shot nudge for "complex task, started editing with ZERO context". The
// agent's FIRST tool action on a multi-file/feature task was a write/edit with
// no prior semantic_search/search_brain/grep_search/read_file. This is the
// context-first ("plan-first via TOOLS") safety net: tell it to gather context
// FIRST, then edit. NOT a prose-plan instruction (that would regress audit-mode)
// — it routes the model to TOOLS. One-shot (contextNudged) so it never loops and
// never fires on trivial tasks (looksLikeComplexTask gates it).
export const CONTEXT_FIRST_NUDGE = [
  `${NUDGE_MARKER} Tarefa COMPLEXA/multi-arquivo: você começou a editar SEM entender o contexto.`,
  'ANTES de editar, reúna contexto com FERRAMENTAS (não prosa):',
  'semantic_search (conceitos/fluxos) e/ou search_brain (decisões anteriores) + read_file nos arquivos-alvo.',
  'Isso é o seu "planejamento" — feito via tool calls. Depois volte a editar com apply_edit/write_file.',
  'Responda com tool calls, não com um plano em texto.',
].join(' ');

export const COMMIT_NUDGE = [
  `${NUDGE_MARKER} Você editou arquivos mas NÃO fechou o ciclo — editar não conta como concluído.`,
  'FECHE O CICLO AGORA via run_command, em ordem:',
  '(1) se estiver no main, `git checkout -b fix/<resumo-kebab>`;',
  '(2) `git add -A && git commit -m "tipo(area): resumo"`;',
  '(3) `git push -u origin HEAD && gh pr create --fill`.',
  'Responda com tool calls, não só texto. PROIBIDO --no-verify e git push --force.',
].join(' ');

/** OpenRouter/provider billing failures — surface clearly in UI. */
export function isBillingOrCreditsError(msg) {
  const s = String(msg || '').toLowerCase();
  return /insufficient credits|cr[eé]ditos insuficientes|never purchased credits|purchase more|key limit exceeded|total limit|402\b|payment required|billing/.test(s);
}

export function billingErrorNotice(msg) {
  const s = String(msg || '');
  const provider = /openrouter/i.test(s) ? 'OpenRouter' : 'Provider';
  if (/key limit exceeded|total limit/i.test(s)) {
    const link = s.match(/https:\/\/openrouter\.ai[^\s)]+/)?.[0];
    return [
      `💳 ${provider}: limite de gasto da **key** esgotado (Credit limit).`,
      link ? `Aumente o limite: ${link}` : 'Abra https://openrouter.ai/settings/keys e aumente o Credit limit da key Apolo.',
      'Ou crie uma key nova com limite maior / use modelo local (Ollama).',
    ].join(' ');
  }
  return [
    `💳 ${provider}: créditos insuficientes ou conta sem saldo.`,
    'Abra https://openrouter.ai/settings/credits ou troque o modelo em Conexões.',
    'Enquanto isso, use um modelo local (Ollama/LM Studio) se disponível.',
  ].join(' ');
}
